import {useCallback, useRef} from 'react'
import {getRandomId} from '../helpers'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

const useRequestManager = (
  // State values
  {activeRequestId, prismProgram},
  // State setters
  {setActiveRequestId, setIsRequestActive, setShowLoader, setMessages},
  // Functions and external dependencies
  {post, handleAutoScrollToBottom}
) => {
  const cancellingRef = useRef(false)

  const showChatLoader = () => {
    setShowLoader(true)
  }

  const hideChatLoader = () => {
    setShowLoader(false)
  }

  const getAPIFailureResponse = useCallback(
    (category) => {
      return {
        id: getRandomId(),
        type: 'Bot',
        label: `${prismProgram} Agent`,
        category,
        responseText: 'Oops, something went wrong on our end. Please try again in a few moments.'
      }
    },
    [prismProgram]
  )

  const populateErrorResponse = useCallback(
    (errResponse) => {
      const data = errResponse?.response
      if (data && Array.isArray(data) && data.length > 0) {
        for (const [index, errText] of data.entries()) {
          const errorResponse = {
            id: getRandomId(),
            type: 'Bot',
            label: index === 0 ? `${prismProgram} Agent` : '',
            responseText: errText
          }
          setMessages((prevMessages) => [...prevMessages, errorResponse])
        }
      } else {
        setMessages((prevMessages) => [...prevMessages, getAPIFailureResponse(prismProgram)])
      }
      handleAutoScrollToBottom()
    },
    [prismProgram, setMessages, getAPIFailureResponse, handleAutoScrollToBottom]
  )

  const handleCancelRequest = useCallback(() => {
    // Prevent multiple simultaneous cancellations
    if (cancellingRef.current) {
      return
    }

    cancellingRef.current = true

    // Store the current request ID before clearing it
    const currentRequestId = activeRequestId

    // Immediately update UI state for responsive UX
    setActiveRequestId(null)
    setIsRequestActive(false)
    setShowLoader(false)

    // Remove streaming messages completely instead of showing empty bubbles
    setMessages((prevMessages) => prevMessages.filter((msg) => !msg.isStreaming))

    // Track analytics for cancellation
    AnalyticsHandler.onChatbotButtonClick({
      button: {name: 'Cancel Request'}
    })

    // Send cancel request to backend asynchronously (fire and forget)
    if (currentRequestId) {
      post(`/cancel/${currentRequestId}`, {}).catch(() => {
        // Silent fail - UI is already updated
      })
    }

    // Reset the cancelling flag after a short delay
    setTimeout(() => {
      cancellingRef.current = false
    }, 100)
  }, [activeRequestId, post, setActiveRequestId, setIsRequestActive, setShowLoader, setMessages])

  return {
    showChatLoader,
    hideChatLoader,
    getAPIFailureResponse,
    populateErrorResponse,
    handleCancelRequest
  }
}

export default useRequestManager

/*
CHATBOT REQUEST MANAGER HOOK

KEY FUNCTIONS:
- showChatLoader/hideChatLoader: Controls loading indicator visibility
- getAPIFailureResponse: Creates standardized error response objects
- populateErrorResponse: Adds error messages to chat conversation
- handleCancelRequest: Cancels active streaming requests with cleanup

FEATURES:
- Request state management
- Error handling and recovery
- Loading indicator control
- Request cancellation with proper cleanup
- Analytics integration for user interactions
*/
